import { useState, useMemo, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useCurrentUserCompany } from "@/hooks/useCurrentUserCompany";
import { useData } from "@/hooks/useData";
import { useDistributionData } from "@/hooks/distribution/useDistributionData";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Package, Zap, Droplets } from "lucide-react";
import { FootprintDocument, FootprintProduct } from "@/types/results";

interface DistributionStep1FormProps {
  onNext: (selectedProduct: FootprintProduct, footprintType: 'carbon' | 'hydric') => void;
  onClose: () => void;
  initialProductId?: string;
  initialFootprintType?: 'carbon' | 'hydric' | '';
}

interface CalculatedProduct {
  id: string;
  name: string;
  product_type: string;
  stone_type_name: string;
  year: number;
  footprint_id: string;
  hasCarbonFootprint: boolean;
  hasHydricFootprint: boolean;
  availableFootprintTypes: Array<'carbon' | 'hydric'>;
}

export const DistributionProductStep = ({ 
  onNext, 
  onClose, 
  initialProductId = "", 
  initialFootprintType = "" 
}: DistributionStep1FormProps) => {
  const { t } = useLanguage();
  const { company, loading: companyLoading } = useCurrentUserCompany();
  const { distributionData } = useDistributionData();
  
  // Form state
  const [selectedProductId, setSelectedProductId] = useState<string>(initialProductId);
  const [selectedFootprintType, setSelectedFootprintType] = useState<'carbon' | 'hydric' | ''>(initialFootprintType);
  const [errors, setErrors] = useState<{ product?: string; footprintType?: string }>({});

  // Update state when initial values change
  useEffect(() => {
    setSelectedProductId(initialProductId);
    setSelectedFootprintType(initialFootprintType);
    setErrors({});
  }, [initialProductId, initialFootprintType]);

  // Fetch data from Firebase
  const { data: footprintDocuments, loading: footprintsLoading } = useData<FootprintDocument>({
    collectionName: "footprints",
  });

  const { data: footprintProducts, loading: productsLoading } = useData<FootprintProduct>({
    collectionName: "footprint_products",
  });

  // Filter and organize data for current company
  const calculatedProducts: CalculatedProduct[] = useMemo(() => {
    if (companyLoading || !company?.id) {
      return [];
    }

    // Filter footprint documents for the current company with submitted status
    const companyFootprints = footprintDocuments?.filter((fp) => 
      fp.company_id === company.id && fp.status === "submitted"
    ) || [];

    // Get all footprint IDs for this company
    const companyFootprintIds = companyFootprints.map((fp) => fp.id);

    // Filter products that belong to this company's footprints
    const companyProducts = footprintProducts?.filter((fp) => 
      companyFootprintIds.includes(fp.footprint_id)
    ) || [];

    // Transform to calculated products format
    const products: CalculatedProduct[] = companyProducts.map((product) => {
      const footprint = companyFootprints.find((fp) => fp.id === product.footprint_id);
      
      // Get existing distribution data for the product
      const existingDistributions = distributionData.filter(dist => dist.product_id === product.id);
      const existingFootprintTypes = existingDistributions.map(dist => dist.footprint_type);
      
      // Calculate available footprint types
      const availableFootprintTypes: Array<'carbon' | 'hydric'> = [];
      
      if ((product.carbon_footprint_total || 0) > 0 && !existingFootprintTypes.includes('carbon')) {
        availableFootprintTypes.push('carbon');
      }
      
      if ((product.hydric_footprint_total || 0) > 0 && !existingFootprintTypes.includes('hydric')) {
        availableFootprintTypes.push('hydric');
      }
      
      return {
        id: product.id,
        name: product.name || t("results.distribution.step1.unnamedProduct"),
        product_type: product.product_type || t("results.distribution.step1.unspecifiedType"),
        stone_type_name: product.stone_type_name || t("results.distribution.step1.unspecifiedStone"),
        year: footprint?.year || new Date().getFullYear(),
        footprint_id: product.footprint_id,
        hasCarbonFootprint: (product.carbon_footprint_total || 0) > 0,
        hasHydricFootprint: (product.hydric_footprint_total || 0) > 0,
        availableFootprintTypes,
      };
    });

    // Only return products that have at least one available footprint type
    return products.filter((product) => product.availableFootprintTypes.length > 0);
  }, [company?.id, footprintDocuments, footprintProducts, companyLoading, distributionData, t]);

  // Get selected product
  const selectedProduct = useMemo(() => {
    return calculatedProducts.find((p) => p.id === selectedProductId);
  }, [selectedProductId, calculatedProducts]);

  // Get available footprint types for selected product
  const availableFootprintTypes = useMemo(() => {
    if (!selectedProduct) return [];

    const types: Array<{ value: 'carbon' | 'hydric', label: string, icon: React.ReactNode }> = [];
    
    // Only show footprint types that are available (not already have distribution data)
    selectedProduct.availableFootprintTypes.forEach(footprintType => {
      if (footprintType === 'carbon') {
        types.push({
          value: 'carbon',
          label: t("results.tabs.carbon"),
          icon: <Zap className="h-4 w-4" />
        });
      }
      
      if (footprintType === 'hydric') {
        types.push({
          value: 'hydric',
          label: t("results.tabs.hydrics"),
          icon: <Droplets className="h-4 w-4" />
        });
      }
    });
    
    return types;
  }, [selectedProduct, t]);

  // Reset footprint type when product changes
  const handleProductChange = (productId: string) => {
    setSelectedProductId(productId);
    setSelectedFootprintType('');
    setErrors({});
  };

  // Handle footprint type change
  const handleFootprintTypeChange = (type: string) => {
    setSelectedFootprintType(type as 'carbon' | 'hydric');
    setErrors({});
  };

  // Validate form
  const validateForm = () => {
    const newErrors: { product?: string; footprintType?: string } = {};

    if (!selectedProductId) {
      newErrors.product = t("results.distribution.step1.validation.productRequired");
    }

    if (!selectedFootprintType) {
      newErrors.footprintType = t("results.distribution.step1.validation.footprintTypeRequired");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle next step
  const handleNext = () => {
    if (!validateForm()) return;

    const selectedProduct = footprintProducts?.find((p) => p.id === selectedProductId);
    if (!selectedProduct) return;
    
    onNext(selectedProduct, selectedFootprintType as 'carbon' | 'hydric');
  };

  // Loading state
  const loading = footprintsLoading || productsLoading || companyLoading;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
        <span className="ml-2 text-gray-600">{t("common.loading")}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Step 1: Product Selection */}
      <Card>
        <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-teal-600" />
              {t("results.distribution.step1.title")}
            </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="product-select" className="required">
              {t("results.distribution.step1.productLabel")} *
            </Label>
            <Select value={selectedProductId} onValueChange={handleProductChange}>
              <SelectTrigger className={errors.product ? "border-red-500" : ""}>
                <SelectValue placeholder={t("results.distribution.step1.productPlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {calculatedProducts
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((product) => (
                    <SelectItem key={product.id} value={product.id}>
                      {product.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            {errors.product && (
              <p className="text-red-500 text-sm">{errors.product}</p>
            )}
          </div>

          {/* Selected Product Information */}
          {selectedProduct && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                  <Package className="h-5 w-5 text-teal-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1">
                    {selectedProduct.name}
                  </h3>
                  <div className="space-y-1 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{t("results.distribution.step1.type")}:</span>
                      <span>{selectedProduct.product_type}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{t("results.distribution.step1.stone")}:</span>
                      <span>{selectedProduct.stone_type_name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{t("results.distribution.step1.year")}:</span>
                      <span>{selectedProduct.year}</span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex gap-2">
                    {selectedProduct.hasCarbonFootprint && (
                      <div className={`flex items-center gap-1 text-xs px-2 py-1 rounded ${
                        selectedProduct.availableFootprintTypes.includes('carbon')
                          ? 'bg-blue-50' 
                          : 'bg-gray-100'
                      }`}>
                        <Zap className={`h-3 w-3 ${
                          selectedProduct.availableFootprintTypes.includes('carbon')
                            ? 'text-blue-600' 
                            : 'text-gray-400'
                        }`} />
                        <span className={`font-medium ${
                          selectedProduct.availableFootprintTypes.includes('carbon')
                            ? 'text-blue-700' 
                            : 'text-gray-500'
                        }`}>
                          CO₂
                        </span>
                        {!selectedProduct.availableFootprintTypes.includes('carbon') && (
                          <span className="text-xs text-gray-400 ml-1">({t("results.distribution.step1.exists")})</span>
                        )}
                      </div>
                    )}
                    {selectedProduct.hasHydricFootprint && (
                      <div className={`flex items-center gap-1 text-xs px-2 py-1 rounded ${
                        selectedProduct.availableFootprintTypes.includes('hydric')
                          ? 'bg-green-50' 
                          : 'bg-gray-100'
                      }`}>
                        <Droplets className={`h-3 w-3 ${
                          selectedProduct.availableFootprintTypes.includes('hydric')
                            ? 'text-green-600' 
                            : 'text-gray-400'
                        }`} />
                        <span className={`font-medium ${
                          selectedProduct.availableFootprintTypes.includes('hydric')
                            ? 'text-green-700' 
                            : 'text-gray-500'
                        }`}>
                          H₂O
                        </span>
                        {!selectedProduct.availableFootprintTypes.includes('hydric') && (
                          <span className="text-xs text-gray-400 ml-1">({t("results.distribution.step1.exists")})</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {calculatedProducts.length === 0 && (
            <Alert>
              <AlertDescription>
                {t("results.distribution.step1.noProducts")}
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Step 2: Footprint Type Selection */}
      {selectedProductId && (
          <Card>
            <CardHeader>
              <CardTitle>{t("results.distribution.step1.footprintTypeTitle")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="required">
                  {t("results.distribution.step1.footprintTypeLabel")} *
                </Label>
              <div className={errors.footprintType ? "border border-red-500 rounded-md p-2" : ""}>
                <div className="grid grid-cols-1 gap-3">
                  {availableFootprintTypes.map((type) => (
                    <Button
                      key={type.value}
                      type="button"
                      variant={selectedFootprintType === type.value ? "default" : "outline"}
                      onClick={() => handleFootprintTypeChange(type.value)}
                      className={`flex items-center gap-2 justify-start h-12 ${
                        selectedFootprintType === type.value 
                          ? "bg-teal-600 hover:bg-teal-700 text-white" 
                          : "border-gray-300 hover:border-teal-300"
                      }`}
                    >
                      {type.icon}
                      {type.label}
                    </Button>
                  ))}
                </div>
              </div>
              {errors.footprintType && (
                <p className="text-red-500 text-sm">{errors.footprintType}</p>
              )}
            </div>

            {availableFootprintTypes.length === 0 && (
              <Alert>
                <AlertDescription>
                  {t("results.distribution.step1.noFootprintTypes")}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button
          variant="outline"
          onClick={onClose}
        >
          {t("common.cancel")}
        </Button>
        <Button
          onClick={handleNext}
          disabled={!selectedProductId || !selectedFootprintType}
          className="bg-teal-600 hover:bg-teal-700 text-white"
        >
          {t("common.next")}
        </Button>
      </div>
    </div>
  );
};
